import {
	Bot,
	Check,
	ChevronDown,
	ChevronRight,
	CircleDollarSign,
	Copy,
	ExternalLink,
	GitBranch,
	Paperclip,
	ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { VariantBadge } from '@/components/atoms/VariantBadge';
import { AgentEventItem } from '@/components/molecules/AgentEventItem';
import { MessageContent } from '@/components/molecules/MessageContent';
import { EvaluationsPanel } from '@/components/organisms/EvaluationsPanel';
import type { Message, MessageAttachment, ToolCall } from '@/schemas/commons';
import type { AgentEvent } from '@/services/Conversations';
import { copyToClipboard } from '@/utils/clipboard';
import { decamelizeKey } from '@/utils/decamelize';

import { INTERCOM_CHANNEL, intercomConversationUrl } from './ConversationDetail.constants';
import { ConversationDetailLoading } from './ConversationDetail.loading';
import { useConversation, useConversationEvents } from './ConversationDetail.requests';
import { styles } from './ConversationDetail.styles';

const formatTime = (iso: string | null | undefined): string => {
	if (!iso || iso === 'unknown') return '—';
	const d = new Date(iso);

	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const hasContent = (m: Message) =>
	m.content?.trim().length > 0 || (m.toolCalls?.length ?? 0) > 0 || (m.attachments?.length ?? 0) > 0;

const formatDateTime = (iso: string | null | undefined): string => {
	if (!iso || iso === 'unknown') return '—';
	const d = new Date(iso);

	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleString('es-CL', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

export const ConversationDetail = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const { threadId } = useParams();
	const { data: conv, isLoading, isError } = useConversation(threadId ?? '');
	// Trace interno (agent_events + variantes). No bloquea el render: sin
	// eventos la página se comporta exactamente como antes.
	const { data: trace } = useConversationEvents(threadId ?? '');
	const events = trace?.events ?? [];
	const variants = trace?.variants ?? {};

	if (isLoading) return <ConversationDetailLoading />;
	if (isError || !conv) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<Link to="/conversations" className="hover:text-default">
						{t('BREADCRUMB_PARENT')}
					</Link>
					<ChevronRight className="h-sm w-sm" />
					<span className="font-mono font-medium text-default">{conv.threadId}</span>
				</nav>
				<div className="flex items-center gap-xs">
					<CopyIdButton threadId={conv.threadId} />
					{conv.channel.toLowerCase() === INTERCOM_CHANNEL && (
						<a
							href={intercomConversationUrl(conv.threadId)}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-3xs rounded-md border border-subtle bg-white px-xs py-3xs text-[11px] text-default transition-colors hover:bg-grey-25"
						>
							<ExternalLink className="h-sm w-sm" />
							{t('OPEN_INTERCOM')}
						</a>
					)}
				</div>
			</header>

			<main className={styles.content}>
				<header className="flex flex-wrap items-start justify-between gap-md">
					<div className="flex items-start gap-xs">
						<div className={styles.userHeaderAvatar}>{conv.userInitials}</div>
						<div>
							<p className="mb-2xs flex items-center gap-2xs text-[11px] font-medium uppercase tracking-wider text-purple-700">
								{t('EYEBROW')}
								<code className="font-mono text-[11px] normal-case tracking-normal text-subtle">{conv.threadId}</code>
							</p>
							<h1 className={`${styles.pageH1} flex items-center gap-xs`}>
								{conv.userName}
								<StatusInline status={conv.status} />
							</h1>
							<p className="mt-2xs text-[13px] text-subtle">
								{conv.companyName} · 🇨🇱 {conv.country} ·{' '}
								<Link
									to={`/modules/${conv.moduleName}`}
									className="text-default transition-colors hover:text-purple-700"
								>
									{conv.moduleName} / {conv.agentName}
								</Link>
							</p>
						</div>
					</div>

					<div className="flex flex-col items-end gap-xs">
						<div className={styles.costCard}>
							<p className="mb-2xs flex items-center justify-end gap-3xs text-[10px] uppercase tracking-wider text-purple-700">
								<CircleDollarSign className="h-sm w-sm" />
								{t('THREAD_COST')}
							</p>
							<p className="text-[22px] font-semibold leading-none tabular-nums text-purple-700">
								${conv.costUsd.toFixed(3)}
							</p>
							<p className="mt-3xs text-[11px] tabular-nums text-subtle">
								{t('TOKENS', { in: conv.tokensIn, out: conv.tokensOut, tools: conv.toolCallCount })}
							</p>
						</div>
						<VariantBadges variants={variants} />
					</div>
				</header>

				<dl className="grid grid-cols-2 gap-x-xl gap-y-md border-y border-subtle py-md md:grid-cols-3 lg:grid-cols-6">
					<div className="flex flex-col gap-3xs">
						<dt className={styles.kvpLabel}>{t('USER_ID')}</dt>
						<dd className={`${styles.kvpValue} font-mono`}>{conv.userId}</dd>
					</div>
					<div className="flex flex-col gap-3xs">
						<dt className={styles.kvpLabel}>{t('CHANNEL')}</dt>
						<dd className={styles.kvpValue}>{conv.channel}</dd>
					</div>
					<div className="flex flex-col gap-3xs">
						<dt className={styles.kvpLabel}>{t('COMPANY_ID')}</dt>
						<dd className={`${styles.kvpValue} font-mono`}>{conv.companyId}</dd>
					</div>
					<div className="flex flex-col gap-3xs">
						<dt className={styles.kvpLabel}>{t('STARTED')}</dt>
						<dd className={`${styles.kvpValue} tabular-nums`}>{formatDateTime(conv.startedAt)}</dd>
					</div>
					<div className="flex flex-col gap-3xs">
						<dt className={styles.kvpLabel}>{t('LAST_ACTIVITY')}</dt>
						<dd className={styles.kvpValue}>{conv.lastActivity}</dd>
					</div>
					<div className="flex flex-col gap-3xs">
						<dt className={styles.kvpLabel}>{t('TURNS')}</dt>
						<dd className={`${styles.kvpValue} tabular-nums`}>{conv.messages.filter(hasContent).length}</dd>
					</div>
				</dl>

				<Transcript messages={conv.messages} events={events} />

				<EvaluationsPanel threadId={conv.threadId} />

				<RawStateBlock rawState={conv.rawState} />
			</main>
		</>
	);
};

const CopyIdButton = ({ threadId }: { threadId: string }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const [copied, setCopied] = useState(false);

	const handleClick = async () => {
		const ok = await copyToClipboard(threadId);

		if (ok) {
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1500);
		}
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			aria-live="polite"
			className={`inline-flex items-center gap-3xs text-[11px] transition-colors ${
				copied ? 'text-success' : 'text-subtle hover:text-default'
			}`}
		>
			{copied ? <Check className="h-sm w-sm" /> : <Copy className="h-sm w-sm" />}
			{copied ? t('COPIED') : t('COPY_ID')}
		</button>
	);
};

const StatusInline = ({ status }: { status: string }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });

	if (status === 'active') {
		return (
			<span className="inline-flex items-center gap-3xs text-[12px] font-normal text-subtle">
				<span className="inline-block h-2xs w-2xs rounded-full bg-green-500" />
				{t('STATUS_ACTIVE')}
			</span>
		);
	}
	if (status === 'closed') return <span className="text-[12px] font-normal text-subtle">{t('STATUS_CLOSED')}</span>;
	if (status === 'handoff')
		return (
			<span className="inline-flex items-center gap-3xs text-[12px] font-normal text-warning">
				{t('STATUS_HANDOFF')}
			</span>
		);
	if (status === 'tool_error')
		return (
			<span className="inline-flex items-center gap-3xs text-[12px] font-normal text-error">
				{t('STATUS_TOOL_ERROR')}
			</span>
		);
	return null;
};

type TranscriptProps = { messages: Message[]; events: AgentEvent[] };

type TimelineEntry = { kind: 'message'; msg: Message } | { kind: 'thinking'; events: AgentEvent[] };

const entryTime = (iso: string | null | undefined): number => {
	const ts = new Date(iso ?? '').getTime();

	return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
};

/**
 * Interleaves transcript messages with internal agent events by timestamp and
 * folds consecutive events into a single "thinking" group. On equal
 * timestamps events sort first: a turn's events happen before the AI reply
 * they feed.
 */
const buildTimeline = (messages: Message[], events: AgentEvent[]): TimelineEntry[] => {
	const items = [
		...messages.map((msg) => ({ time: entryTime(msg.createdAt), order: 1, entry: { kind: 'message', msg } as const })),
		...events.map((ev) => ({ time: entryTime(ev.occurredAt), order: 0, entry: { kind: 'event', ev } as const })),
	].sort((a, b) => a.time - b.time || a.order - b.order);

	return items.reduce<TimelineEntry[]>((acc, item) => {
		if (item.entry.kind === 'message') return [...acc, { kind: 'message', msg: item.entry.msg }];
		const last = acc.at(-1);

		if (last?.kind === 'thinking')
			return [...acc.slice(0, -1), { kind: 'thinking', events: [...last.events, item.entry.ev] }];
		return [...acc, { kind: 'thinking', events: [item.entry.ev] }];
	}, []);
};

const Transcript = ({ messages, events }: TranscriptProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const [showToolCalls, setShowToolCalls] = useState(true);
	const [showMetadata, setShowMetadata] = useState(false);
	const [showThinking, setShowThinking] = useState(true);
	const visible = messages.filter(hasContent);
	const humans = visible.filter((m) => m.role === 'human').length;
	const ais = visible.filter((m) => m.role === 'ai').length;
	const tools = visible.reduce((acc, m) => acc + (m.toolCalls?.length ?? 0), 0);
	const timeline = buildTimeline(visible, showThinking ? events : []);
	const subtitle =
		events.length > 0 && showThinking
			? t('TIMELINE_SUB_EVENTS', { total: visible.length, events: events.length })
			: t('TRANSCRIPT_SUB', { total: visible.length, humans, ais, tools });

	return (
		<section className="flex flex-col gap-md">
			<header className="flex items-baseline justify-between">
				<div>
					<h2 className={styles.sectionH}>{t('TRANSCRIPT_TITLE')}</h2>
					<p className={styles.sectionSub}>{subtitle}</p>
				</div>
				<div className="flex items-center gap-md text-[11px] text-subtle">
					{events.length > 0 && (
						<label htmlFor="toggle-thinking" className="flex cursor-pointer items-center gap-3xs">
							<input
								id="toggle-thinking"
								type="checkbox"
								checked={showThinking}
								onChange={(e) => setShowThinking(e.target.checked)}
								className="accent-purple-500"
							/>
							{t('SHOW_THINKING')}
						</label>
					)}
					<label htmlFor="toggle-tool-calls" className="flex cursor-pointer items-center gap-3xs">
						<input
							id="toggle-tool-calls"
							type="checkbox"
							checked={showToolCalls}
							onChange={(e) => setShowToolCalls(e.target.checked)}
							className="accent-purple-500"
						/>
						{t('SHOW_TOOL_CALLS')}
					</label>
					<label htmlFor="toggle-metadata" className="flex cursor-pointer items-center gap-3xs">
						<input
							id="toggle-metadata"
							type="checkbox"
							checked={showMetadata}
							onChange={(e) => setShowMetadata(e.target.checked)}
							className="accent-purple-500"
						/>
						{t('SHOW_METADATA')}
					</label>
				</div>
			</header>

			<ol className="flex flex-col gap-lg">
				{timeline.map((entry) =>
					entry.kind === 'message' ? (
						<TurnItem key={entry.msg.id} msg={entry.msg} showToolCalls={showToolCalls} showMetadata={showMetadata} />
					) : (
						<ThinkingGroup key={`thinking-${entry.events[0]?.id}`} events={entry.events} />
					),
				)}
			</ol>
		</section>
	);
};

const ThinkingGroup = ({ events }: { events: AgentEvent[] }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const label = events.length === 1 ? t('THINKING_GROUP_ONE') : t('THINKING_GROUP', { count: events.length });

	return (
		<li className="ml-[40px] flex flex-col gap-3xs border-l-2 border-subtle py-3xs pl-md" aria-label={label}>
			<p className="mb-3xs flex items-center gap-2xs text-[10px] font-medium uppercase tracking-wider text-subtle">
				<GitBranch className="h-[12px] w-[12px]" />
				{label}
			</p>
			{events.map((ev) => (
				<AgentEventItem key={ev.id} event={ev} />
			))}
		</li>
	);
};

const VariantBadges = ({ variants }: { variants: Record<string, string> }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	// El cliente API camelliza las keys dinámicas (rag_engine → ragEngine);
	// mostramos el identificador snake_case real para correlación con SQL.
	const entries = Object.entries(variants).map(([k, v]) => [decamelizeKey(k), v] as const);

	if (entries.length === 0) return null;
	return (
		<div className="flex flex-col items-end gap-3xs">
			<p className="text-[10px] uppercase tracking-wider leading-none text-subtle">{t('BOT_VERSION')}</p>
			<div className="flex flex-wrap items-center justify-end gap-2xs">
				{entries.map(([experiment, variant]) => (
					<VariantBadge key={experiment} experiment={experiment} variant={variant} />
				))}
			</div>
		</div>
	);
};

type TurnProps = { msg: Message; showToolCalls: boolean; showMetadata: boolean };

const TurnItem = ({ msg, showToolCalls, showMetadata }: TurnProps) => {
	if (msg.role === 'human') return <HumanTurn msg={msg} showMetadata={showMetadata} />;
	if (msg.role === 'ai') return <AiTurn msg={msg} showToolCalls={showToolCalls} showMetadata={showMetadata} />;
	if (msg.role === 'system') return <SystemTurn msg={msg} showMetadata={showMetadata} />;
	return null;
};

// Stable key without holding the full URL (legacy `data:` URIs run ~1 MB):
// the URL tail is unique per attachment (base64 tail / S3 object key).
const attachmentKey = (att: MessageAttachment) => att.url.slice(-64);

const AttachmentList = ({ attachments }: { attachments: MessageAttachment[] }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const isImage = (att: MessageAttachment) => att.contentType.startsWith('image/');

	return (
		<div className="mt-2xs flex flex-wrap items-start gap-xs">
			{attachments.map((att) =>
				isImage(att) ? (
					<a key={attachmentKey(att)} href={att.url} target="_blank" rel="noreferrer" className="block">
						<img
							src={att.url}
							alt={att.name || t('ATTACHMENT_IMAGE_ALT')}
							loading="lazy"
							className="max-h-[260px] max-w-[340px] rounded-md border border-subtle object-contain"
						/>
					</a>
				) : (
					<a
						key={attachmentKey(att)}
						href={att.url}
						target="_blank"
						rel="noreferrer"
						className="flex items-center gap-2xs rounded-md border border-subtle bg-grey-25 px-xs py-2xs text-[12px] text-default transition-colors hover:border-default"
					>
						<Paperclip className="h-[12px] w-[12px] text-subtle" />
						{att.name || t('ATTACHMENT_PDF_OPEN')}
					</a>
				),
			)}
		</div>
	);
};

const HumanTurn = ({ msg, showMetadata }: { msg: Message; showMetadata: boolean }) => (
	<li className={styles.turn}>
		<div className={styles.turnAvatarUser}>{msg.authorInitials ?? 'U'}</div>
		<div className="min-w-0 flex-1">
			<div className={styles.metaRow}>
				<span className="font-semibold text-default">{msg.authorName}</span>
				{showMetadata && (
					<>
						<span>·</span>
						<span className="tabular-nums">{formatTime(msg.createdAt)}</span>
					</>
				)}
			</div>
			{msg.content.trim().length > 0 && <div className={`${styles.bubble} ${styles.bubbleUser}`}>{msg.content}</div>}
			{(msg.attachments?.length ?? 0) > 0 && <AttachmentList attachments={msg.attachments ?? []} />}
		</div>
	</li>
);

const AiTurn = ({
	msg,
	showToolCalls,
	showMetadata,
}: {
	msg: Message;
	showToolCalls: boolean;
	showMetadata: boolean;
}) => (
	<li className={styles.turn}>
		<div className={styles.turnAvatarAi}>
			<Bot className="h-[14px] w-[14px]" />
		</div>
		<div className="flex min-w-0 flex-1 flex-col gap-xs">
			<div className={styles.metaRow}>
				<span className="font-semibold text-default">{msg.authorName}</span>
				{showMetadata && (
					<>
						{msg.model && (
							<>
								<span className="rounded-md bg-grey-25 px-3xs py-3xs text-[10px] font-medium text-subtle">
									{msg.model}
								</span>
								<span>·</span>
							</>
						)}
						<span className="tabular-nums">{formatTime(msg.createdAt)}</span>
						{msg.costUsd != null && <span className={`ml-auto ${styles.badgeCost}`}>${msg.costUsd.toFixed(3)}</span>}
					</>
				)}
			</div>
			{showToolCalls && msg.toolCalls?.map((tool) => <ToolCallBlock key={tool.id} tool={tool} />)}
			{/* Tool-only turns arrive with empty content (the reply comes in a
			    separate message) — skip the bubble to avoid an empty pill. */}
			{msg.content.trim().length > 0 && (
				<div className={`${styles.bubble} ${styles.bubbleAi}`}>
					<MessageContent content={msg.content} />
				</div>
			)}
		</div>
	</li>
);

const SystemTurn = ({ msg, showMetadata }: { msg: Message; showMetadata: boolean }) => (
	<li className={styles.turn}>
		<div className={styles.turnAvatarSystem}>
			<ShieldCheck className="h-[14px] w-[14px]" />
		</div>
		<div className="min-w-0 flex-1">
			<div className={styles.metaRow}>
				<span className="font-semibold text-subtle">{msg.authorName ?? 'system'}</span>
				{showMetadata && (
					<>
						<span>·</span>
						<span className="tabular-nums">{formatTime(msg.createdAt)}</span>
					</>
				)}
			</div>
			{msg.content.trim().length > 0 && (
				<div className={styles.bubbleSystem}>
					<MessageContent content={msg.content} />
				</div>
			)}
		</div>
	</li>
);

const ToolCallBlock = ({ tool }: { tool: ToolCall }) => (
	<details className={`${styles.bubbleTool} group`} open>
		<summary className="flex cursor-pointer items-center gap-2xs rounded-md px-xs py-2xs transition-colors hover:bg-grey-25">
			<ChevronRight className="h-sm w-sm text-subtle transition-transform group-open:rotate-90" />
			<code className="text-[12px] font-semibold text-default">{tool.name}</code>
			<span className="ml-auto font-sans text-[11px] tabular-nums text-subtle">
				{tool.durationMs}&nbsp;ms · ${tool.costUsd.toFixed(3)}
			</span>
		</summary>
		<div className="flex flex-col gap-xs border-t border-subtle px-xs py-xs">
			<div>
				<p className="mb-3xs font-sans text-[10px] font-medium uppercase tracking-wider text-subtle">Input</p>
				<pre className="overflow-x-auto rounded-md bg-grey-25 px-xs py-xs text-[11px] text-default">
					{JSON.stringify(tool.args, null, 2)}
				</pre>
			</div>
			{tool.output != null && (
				<div>
					<p className="mb-3xs font-sans text-[10px] font-medium uppercase tracking-wider text-subtle">Output</p>
					<pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-grey-25 px-xs py-xs text-[11px] text-default">
						{JSON.stringify(tool.output, null, 2)}
					</pre>
				</div>
			)}
		</div>
	</details>
);

const RawStateBlock = ({ rawState }: { rawState: Record<string, unknown> }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const [open, setOpen] = useState(false);

	return (
		<section className="border-t border-subtle pt-md">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="group flex w-full items-baseline justify-between text-left"
			>
				<div>
					<h2 className={`${styles.sectionH} transition-colors group-hover:text-subtle`}>{t('RAW_STATE_TITLE')}</h2>
					<p className={styles.sectionSub}>{t('RAW_STATE_SUB')}</p>
				</div>
				<ChevronDown className={`h-[14px] w-[14px] text-subtle transition-transform ${open ? 'rotate-180' : ''}`} />
			</button>
			{open && (
				<pre className="mt-md overflow-x-auto rounded-md border border-subtle bg-grey-25 px-xs py-xs text-[11px] text-default">
					{JSON.stringify(rawState, null, 2)}
				</pre>
			)}
		</section>
	);
};
